import React from 'react'
import {
  Route,
  Navigate,
  Outlet,
  createRoutesFromElements,
  useOutlet,
  createBrowserRouter,
  useLocation,
  useParams,
} from 'react-router-dom'
import * as Sentry from '@sentry/react'

import ErrorFallback from '@components/ErrorBoundary/ErrorBoundaryFallback'
import Layout from '@components/Layout'
import Loading from '@components/Loading'
import Toaster from '@components/Toast'

import ContactLayout from '@pages/Accounting/Contacts/components/ContactLayout'
import ContactListLayout from '@pages/Accounting/Contacts/components/ContactListLayout'
import TeamLayout from '@pages/Team/components/Layout'
import Paywall from '@pages/Paywall'

import routes from '@src/Routes/routes'

import { useHasPermission } from '@helpers/hooks/useHasPermission'
import { Perm, TEAM_SETTINGS_PERMS } from '@type/permissions'
import type { PermCodename } from '@type/permissions'
import Auth from '@helpers/AuthRoute'
import lazy from '@helpers/lazy'

import { useGetMyAccount } from '@queries/Account'
import { getLandingRoute } from '@helpers/getLandingRoute'
import TeamHome from '@src/pages/Team/components/TeamHome'
import { useGetShowOrgs } from '@helpers/hooks/useGetShowOrgs'
import CostCenterList from '@src/pages/Accounting/CostCenter/List'
import CostCenterForm from '@src/pages/Accounting/CostCenter/Form'
import CostCenterDetails from '@src/pages/Accounting/CostCenter/Details'
import CostCenterDetailsBudget from '@src/pages/Accounting/CostCenter/Details/components/BudgetTab'
import CostCenterDetailsBreakdown from '@src/pages/Accounting/CostCenter/Details/components/BreakdownTab'

import NotFound from '@pages/NotFound'
import ReportsList from '@pages/Reports/List'
import AIReports from '@pages/Reports/List/AIReports'
import ManualReports from '@pages/Reports/List/ManualReports'
import ReportForm from '@pages/Reports/ReportForm'
import { useGetShowAIReports } from '@helpers/hooks/useGetShowAIReports'
import useGetIsAiAssistantHidden from '@helpers/hooks/useGetIsAiAssistantHidden'
import { useGetShowCustomers } from '@helpers/hooks/useGetShowCustomers'

const LoginPage = lazy(() => import('@src/pages/Authorization/Auth'), 'LoginPage')
const SettingsPage = lazy(() => import('@src/pages/Settings'), 'SettingsPage')
const ProfileSettingsPage = lazy(() => import('@src/pages/ProfileSettings'), 'ProfileSettingsPage')
const TeamSettingsPage = lazy(() => import('@src/pages/TeamSettings'), 'TeamSettingsPage')

const ApprovalRequestsPage = lazy(() => import('@src/pages/ApprovalRequests/RequestsList'), 'ApprovalRequestsPage')
const ActionsPage = lazy(() => import('@src/pages/Actions'), 'ActionsPage')
const ActionDetailPage = lazy(() => import('@src/pages/Actions/ActionDetail'), 'ActionDetailPage')
const ApprovalRequestsDetailPage = lazy(
  () => import('@src/pages/ApprovalRequests/RequestDetails'),
  'ApprovalRequestsDetailPage',
)
const ApprovalRequestsForm = lazy(
  () => import('@src/pages/ApprovalRequests/RequestForm/CustomForm'),
  'ApprovalRequestsForm',
)
const EmailApprove = lazy(() => import('@src/pages/ApprovalRequests/EmailApprove'), 'EmailApprove')

const IntegrationZapierPage = lazy(() => import('@src/pages/Settings/IntegrationZapierPage'), 'IntegrationZapierPage')

const Billing = lazy(() => import('@src/pages/Billing'), 'Billing')

const Onboarding = lazy(() => import('@pages/Onboarding'), 'Onboarding')
const Dashboard = lazy(() => import('@pages/Dashboard'), 'Dashboard')

const PipelineBuilder = lazy(() => import('@pages/Pipelines/Builder'), 'PipelineBuilder')
const PipelinesList = lazy(() => import('@pages/Pipelines/PipelinesList'), 'PipelinesList')
const ProcessDetails = lazy(() => import('@pages/Pipelines/ProcessDetails'), 'ProcessDetails')
const ProcessesList = lazy(() => import('@pages/Pipelines/ProcessesList'), 'ProcessesList')

const File = lazy(() => import('@pages/File'), 'File')

const QuickbooksDisconnected = lazy(
  () => import('@src/vendors/quickbooks/QuickbooksDisconnected'),
  'QuickbooksDisconnected',
)

const OrganizationalStructureList = lazy(() => import('@src/pages/Team/Members/List'), 'OrganizationalStructureList')
const OrganizationalStructureMember = lazy(
  () => import('@src/pages/Team/Members/Member'),
  'OrganizationalStructureMember',
)
const TeamDepartments = lazy(() => import('@src/pages/Team/Departments'), 'TeamDepartments')
const TeamLocations = lazy(() => import('@src/pages/Team/Locations'), 'TeamLocations')
const TeamRoles = lazy(() => import('@src/pages/Team/Roles'), 'TeamRoles')
const Mailbox = lazy(() => import('@pages/Mailbox'), 'Mailbox')

// const RequestorUpgradePopup = lazy(() => import('@src/pages/Billing/RequestorUpgradePopup'))

const VendorsList = lazy(() => import('@src/pages/Accounting/Contacts/ContactsList'), 'VendorsList')
const VendorForm = lazy(() => import('@src/pages/Accounting/Contacts/ContactForm'), 'VendorForm')

const InvoicesList = lazy(() => import('@src/pages/Accounting/Invoices/List'), 'InvoicesList')
const InvoiceDetails = lazy(() => import('@src/pages/Accounting/Invoices/Details'), 'InvoiceDetails')

const FinanceSettingsLayout = lazy(
  () => import('@src/pages/Accounting/FinanceSettings/components/Layout'),
  'FinanceSettingsLayout',
)
const AccountsList = lazy(() => import('@src/pages/Accounting/Accounts/AccountsList'), 'AccountsList')
const CategoriesList = lazy(() => import('@src/pages/Accounting/Accounts/CategoriesList'), 'CategoriesList')
const CompanyDetails = lazy(() => import('@src/pages/Accounting/FinanceSettings/CompanyDetails'), 'CompanyDetails')
const TaxList = lazy(() => import('@src/pages/Accounting/Taxes/TaxList'), 'TaxList')
const ProductsList = lazy(() => import('@src/pages/Accounting/Products/List'), 'ProductsList')
const EntitiesList = lazy(() => import('@src/pages/Accounting/FinanceSettings/Entities'), 'EntitiesList')

const OrganisationsList = lazy(
  () => import('@src/pages/Accounting/Organizations/OrganizationsList'),
  'OrganisationsList',
)
const OrganizationForm = lazy(() => import('@src/pages/Accounting/Organizations/OrganizationForm'), 'OrganizationForm')

const VendorContractsList = lazy(
  () => import('@src/pages/Accounting/Contacts/Contracts/ContractsList'),
  'VendorContractsList',
)
const ContractsList = lazy(() => import('@src/pages/Accounting/Contracts/List'), 'ContractsList')
const ContractForm = lazy(() => import('@src/pages/Accounting/Contacts/Contracts/ContractForm'), 'ContractForm')

const PublicForm = lazy(() => import('@src/pages/ApprovalRequests/RequestForm/PublicForm'), 'PublicForm')

const InstallTeamsGuide = lazy(() => import('@pages/InstallTeamsGuide'), 'InstallTeamsGuide')
const McpConsentPage = lazy(() => import('@pages/McpConsent'), 'McpConsent')

const Tasks = lazy(() => import('@pages/Tasks/List'), 'Tasks')

const Assistant = lazy(() => import('@pages/Assistant'), 'Assistant')

const Version = lazy(() => import('@pages/Version'), 'Version')

const PurchaseOrdersList = lazy(() => import('@src/pages/Accounting/PurchaseOrders/List'), 'PurchaseOrdersList')
const PurchaseOrderDetails = lazy(() => import('@src/pages/Accounting/PurchaseOrders/Details'), 'PurchaseOrderDetails')

const QuotesList = lazy(() => import('@src/pages/Accounting/Quotes/List'), 'QuotesList')
const QuoteDetails = lazy(() => import('@src/pages/Accounting/Quotes/Details'), 'QuoteDetails')

const RFQsList = lazy(() => import('@src/pages/Accounting/RFQ/List'), 'RfqList')
const RFQDetails = lazy(() => import('@src/pages/Accounting/RFQ/Details'), 'RfqDetails')
const RFQProposalDetails = lazy(() => import('@src/pages/Accounting/RFQ/ProposalDetails'), 'RfqProposalDetails')
const RFQProposalForm = lazy(() => import('@src/pages/Accounting/RFQ/ProposalForm'), 'RfqProposalForm')

const AuthLayout = Auth(Layout)

const LoadingContainer = () => {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <React.Suspense fallback={<Loading loading />}>
        <Outlet />
      </React.Suspense>
    </Sentry.ErrorBoundary>
  )
}

/** Route gate for ``/pipelines/:id`` — picks the perm based on whether
 * the param is "new" (create) or an integer id (edit). Without this,
 * a user holding only WORKFLOW_ADD sees the "new pipeline" menu link
 * and gets redirected the moment they click it.
 */
export const PipelineByIdGate: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const isCreate = id === 'new'
  // Creating needs workflow.add. VIEWING an existing workflow needs only
  // workflow.view: the builder renders read-only without workflow.change
  // (see useIsBuilderReadOnly), so view-only roles can inspect the full
  // configuration. Every write is still gated server-side.
  return (
    <ProtectedRoute requirePerm={isCreate ? Perm.WORKFLOW_ADD : Perm.WORKFLOW_VIEW}>
      <PipelineBuilder />
    </ProtectedRoute>
  )
}

/** Route gate for the contact list — the ``contactParty`` URL param
 * decides whether this is the vendor or customer view, so the perm
 * gate must match the sidebar's ``Perm.VENDOR_VIEW`` /
 * ``Perm.CUSTOMER_VIEW`` instead of falling through to
 * ``ACCOUNTING_MANAGE`` which would redirect view-only users.
 */
const ContactsListGate: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { contactParty } = useParams<{ contactParty?: string }>()
  // Only 'customer' / 'vendor' (or legacy undefined → vendor) are valid; a
  // malformed party must not silently authorize as a vendor route.
  if (contactParty && contactParty !== 'customer' && contactParty !== 'vendor') {
    return <Navigate to={routes.public.notFound} replace />
  }
  const perm = contactParty === 'customer' ? Perm.CUSTOMER_VIEW : Perm.VENDOR_VIEW
  return <ProtectedRoute requirePerm={perm}>{children}</ProtectedRoute>
}

// Legacy /settings/* paths, kept alive for old links and for backend OAuth
// callbacks that arrive carrying ?integration=. `<Navigate to="<string>">`
// resolves a bare pathname and drops the query string, which silently
// half-completes a connect: the integration is written, but the panel that
// links the tenant to an accounting entity never mounts. Forward `search`.
export const LegacyRedirect: React.FC<{ to: string }> = ({ to }) => {
  const { search } = useLocation()

  return <Navigate to={{ pathname: to, search }} replace />
}

const LEGACY_SETTINGS_REDIRECTS: ReadonlyArray<readonly [from: string, to: string]> = [
  ['/settings', '/profile'],
  ['/settings/profile', '/profile'],
  ['/settings/security', '/profile/security'],
  ['/settings/team', '/team-settings'],
  ['/settings/integration', '/team-settings/integration'],
  ['/settings/notifications', '/profile/notifications'],
]

// Exported so the test can mount these exact <Route> elements rather than a
// hand-copied mapping. A duplicate table in the test would stay green if the
// real routes regressed to a query-dropping <Navigate>.
// The three paths that render a single approval request. Exported for the
// same reason as legacySettingsRoutes below: a test that re-declared them
// would stay green while the real table regressed, and this exact gate has
// regressed once already (the base route was ungated, the two /:action
// routes were not, so a named approver could open the request and then get
// redirected away the moment they clicked Approve).
export const APPROVAL_REQUEST_DETAIL_PATHS: readonly string[] = [
  routes.private.approvalRequestsById,
  routes.private.approvalRequestsByIdAction,
  routes.private.approvalRequestsByIdUuidAction,
]

export const approvalRequestDetailRoutes = () =>
  APPROVAL_REQUEST_DETAIL_PATHS.map((path) => (
    <Route
      key={path}
      path={path}
      element={
        <ProtectedRoute>
          <ApprovalRequestsDetailPage />
        </ProtectedRoute>
      }
    />
  ))

export const legacySettingsRoutes = () =>
  LEGACY_SETTINGS_REDIRECTS.map(([from, to]) => <Route key={from} path={from} element={<LegacyRedirect to={to} />} />)

export const ProtectedRoute: React.FC<
  React.PropsWithChildren<{
    // Feature-flag gates (NOT authz — these check whether the team has
    // the feature enabled at all, not whether the user has perms).
    onlyWithOrgsAvailable?: boolean
    onlyWithReportsAvailable?: boolean
    // ADN-484 beta. The nav item is already hidden by requiresMcpActions,
    // but a typed or bookmarked URL would still render the page shell for
    // a team without the flag — every API call 404s, so it reads as a
    // broken feature rather than one they do not have.
    onlyWithMcpActions?: boolean
    // RBAC: require the user's role(s) to grant this permission to
    // access the route. Redirects to the user's landing route if
    // missing. Replaces the legacy `adminOnly` / `userOnly` /
    // `checkWithPrivacySettings` flags — every route now expresses
    // access via the codename that authorizes it, so custom roles
    // work without manual nav-item carve-outs.
    requirePerm?: PermCodename
    // RBAC: admit the user if their role(s) grant ANY of these perms.
    // Use for areas reachable by several granular perms (e.g. Team
    // Settings, whose tabs are each gated by a different codename) so a
    // single blanket gate doesn't shadow the narrower ones.
    requireAnyPerm?: PermCodename[]
  }>
> = ({
  children,
  onlyWithOrgsAvailable,
  onlyWithReportsAvailable,
  onlyWithMcpActions,
  requirePerm,
  requireAnyPerm,
}) => {
  const { data: user } = useGetMyAccount()
  const isOrgShown = useGetShowOrgs()
  const showReports = useGetShowAIReports()
  // Assistant availability for the redirect fallback is the hidden escape
  // hatch, NOT the chatbot flag — teams with the flag off still have the
  // /assistant teaser page, and it's the loop-free landing surface.
  const aiAssistantHidden = useGetIsAiAssistantHidden()
  const showCustomers = useGetShowCustomers()
  // Hooks must be called unconditionally. We always run useHasPermission
  // and ignore the result when requirePerm isn't set, so React doesn't
  // see the hook call order change between renders.
  const hasRequiredPerm = useHasPermission(requirePerm ?? Perm.WORKFLOW_VIEW)

  const outlet = useOutlet()

  // Wait for /me to resolve before making the redirect decision.
  // Without this guard, an authorized user hitting a deep-link or
  // hard-refreshing got bounced to the landing route because
  // useHasPermission fails closed on `data: undefined`. Render
  // nothing during the in-flight window (the AppLayout above already
  // shows a top-level loader, so the page doesn't look broken).
  if (user === undefined) {
    return null
  }

  const userPerms = user.permissions ?? []
  const lacksAnyPerm = requireAnyPerm !== undefined && !requireAnyPerm.some((p) => userPerms.includes(p))

  const shouldRedirect =
    (onlyWithOrgsAvailable && !isOrgShown) ||
    (onlyWithReportsAvailable && !showReports) ||
    (onlyWithMcpActions && !user.team?.settings?.mcp_actions_enabled) ||
    (requirePerm !== undefined && !hasRequiredPerm) ||
    lacksAnyPerm

  if (shouldRedirect) {
    // The user's first accessible nav item — Dashboard for roles that
    // grant request.view, something else (e.g. Team Members for an HR
    // Admin) when Dashboard is also gated out. See getLandingRoute.
    // ``replace`` keeps the denied route out of browser history so the
    // back button doesn't bounce the user back here and re-trigger the
    // redirect in a loop.
    return <Navigate to={getLandingRoute(user, !aiAssistantHidden, showCustomers)} replace />
  }

  return <>{children || outlet}</>
}

const Wrapper = () => {
  const location = useLocation()

  return (
    <>
      {![routes.public.login, routes.public.signup].includes(location.pathname) && (
        <title>Approveit - Workflow Automation Platform for Business Operations</title>
      )}

      <Toaster />
      <Outlet />
    </>
  )
}

const Router = createRoutesFromElements(
  <Route element={<Wrapper />}>
    <Route element={<LoadingContainer />}>
      <Route path={routes.public.signup} element={<LoginPage />} />
      <Route path={routes.public.login} element={<LoginPage />} />
      <Route path={routes.public.activate} element={<LoginPage />} />
      <Route path={routes.public.forgotPassword} element={<LoginPage />} />
      <Route path={routes.public.sso} element={<LoginPage />} />
      <Route path={routes.public.approvalRequestApproveReject} element={<EmailApprove />} />
      <Route path={routes.public.approvalRequestApproveRejectPublic} element={<EmailApprove />} />
      <Route path={routes.public.quickbooksDisconnected} element={<QuickbooksDisconnected />} />
      <Route path={routes.public.publicForm} element={<PublicForm />} />
      <Route path={routes.public.mcpConsent} element={<McpConsentPage />} />
      <Route path={routes.public.installTeams} element={<InstallTeamsGuide />} />
      <Route path={routes.private.rfqProposalForm} element={<RFQProposalForm />} />

      <Route path={routes.public.notFound} element={<NotFound />} />
    </Route>

    <Route path="/" element={<AuthLayout />}>
      <Route path={routes.private.onboarding} element={<Onboarding />} />
      <Route path={routes.private.paywall} element={<Paywall />} />
      {/* Standalone, like onboarding and paywall above: a consent screen
          renders without the app chrome. Inside AuthLayout so an
          unauthenticated visitor is sent through the workspace's normal
          login and returned with the query string. Deliberately NOT
          inside ProtectedRoute — connecting an application to your own
          account is not gated by a team codename, and what the resulting
          token can DO is gated per tool call on the server. */}

      <Route
        path={routes.private.billing}
        element={
          <ProtectedRoute requirePerm={Perm.BILLING_VIEW}>
            <Billing />
          </ProtectedRoute>
        }
      />

      <Route element={<ProtectedRoute />}>
        <Route path={routes.private.file} element={<File />} />
        {/* `/`, `/dashboard`, and `/dashboard/:tab` all route to Dashboard,
            but the page itself is approval-request-centric. Wrap each in a
            ProtectedRoute that requires REQUEST_VIEW so a role without it
            (e.g. HR Admin) is redirected to their first accessible nav item
            by getLandingRoute(). */}
        <Route element={<ProtectedRoute requirePerm={Perm.REQUEST_VIEW} />}>
          <Route path={routes.private['']} element={<Dashboard />} />
          <Route path={routes.private.dashboard} element={<Dashboard />} />
          <Route path={routes.private.dashboardTab} element={<Dashboard />} />
        </Route>
        {/* Legacy settings routes - redirect to new paths */}
        {legacySettingsRoutes()}

        {/* Profile settings (user-specific) */}
        <Route path={routes.private.profile} element={<ProfileSettingsPage />} />
        <Route path={routes.private.profileRouted} element={<ProfileSettingsPage />} />

        {/* Team settings (admin) */}
        <Route
          path={routes.private.teamSettings}
          element={
            <ProtectedRoute requireAnyPerm={TEAM_SETTINGS_PERMS}>
              <TeamSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.teamSettingsRouted}
          element={
            <ProtectedRoute requireAnyPerm={TEAM_SETTINGS_PERMS}>
              <TeamSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.organizationalStructureById}
          element={
            <ProtectedRoute requirePerm={Perm.TEAM_VIEW}>
              <OrganizationalStructureMember />
            </ProtectedRoute>
          }
        />
        <Route element={<TeamLayout />}>
          <Route
            path={routes.private.organizationalStructure}
            element={
              <ProtectedRoute requireAnyPerm={[Perm.TEAM_VIEW, Perm.TEAM_MANAGE_ORG_STRUCTURE, Perm.ROLE_VIEW]}>
                <TeamHome roster={<OrganizationalStructureList />} />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.private.teamDepartments}
            element={
              <ProtectedRoute requirePerm={Perm.TEAM_VIEW}>
                <TeamDepartments />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.private.teamDepartmentById}
            element={
              <ProtectedRoute requirePerm={Perm.TEAM_VIEW}>
                <TeamDepartments />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.private.teamLocations}
            element={
              <ProtectedRoute requirePerm={Perm.TEAM_VIEW}>
                <TeamLocations />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.private.teamLocationById}
            element={
              <ProtectedRoute requirePerm={Perm.TEAM_VIEW}>
                <TeamLocations />
              </ProtectedRoute>
            }
          />
          <Route
            path={routes.private.teamRoles}
            element={
              <ProtectedRoute requirePerm={Perm.ROLE_VIEW}>
                <TeamRoles />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route
          path={routes.private.integrationsZapier}
          element={
            <ProtectedRoute requirePerm={Perm.INTEGRATION_MANAGE}>
              <IntegrationZapierPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={routes.private.approvalRequests}
          element={
            <ProtectedRoute requirePerm={Perm.REQUEST_VIEW}>
              <ApprovalRequestsPage />
            </ProtectedRoute>
          }
        />
        {/* ADN-484 post-approval MCP actions — gated by team mcp_actions_enabled
            in the nav filter; no dedicated RBAC perm yet. */}
        <Route
          path={routes.private.actions}
          element={
            <ProtectedRoute onlyWithMcpActions requirePerm={Perm.REQUEST_VIEW}>
              <ActionsPage />
            </ProtectedRoute>
          }
        />
        {/* No requirePerm, deliberately — unlike the list above.
            The person this page exists for is a DESIGNATED EXECUTOR named
            on the action, and being asked to confirm a tool call does not
            imply holding request.view: an approver reaches a request
            through the approver relationship, not that codename. Gating the
            detail on request.view redirected exactly the user the
            confirmation email had just summoned.

            Authorization is not weakened by dropping it. The row filter is
            visible_execution_queryset on the backend (admin / designated
            executor / requestor), which its own docstring calls the ENTIRE
            authorization for these two endpoints — the /actions endpoints
            are exempted from the codename gate on those grounds. An
            unauthorized id returns nothing and renders detailNotFound. */}
        <Route
          path={routes.private.actionById}
          element={
            <ProtectedRoute onlyWithMcpActions>
              <ActionDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.approvalRequest}
          element={
            <ProtectedRoute requirePerm={Perm.REQUEST_ADD}>
              <ApprovalRequestsForm />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.approvalRequestEditById}
          element={
            <ProtectedRoute requirePerm={Perm.REQUEST_VIEW}>
              <ApprovalRequestsForm />
            </ProtectedRoute>
          }
        />
        {/* All THREE deliberately without requirePerm={Perm.REQUEST_VIEW}.
            Being named on a request admits you to it regardless of the
            codename (see approval_request/permissions.py), and an approval
            notification links straight here — a route-level perm check
            cannot know whether this user is a party to THIS request, so it
            bounced named approvers off the page they were asked to act on.
            The server is the authority: it returns 403/404 for a non-party,
            which the page renders as an error rather than a redirect.

            The two /:action routes render the SAME page, and Approve /
            Reject / Request changes navigate to them. Gating those but not
            the base route is the worst of both: the approver reaches the
            request, clicks Approve, and is redirected away with nothing
            recorded. The matching backend endpoints already carry
            HasPerm(REQUEST_VIEW) | IsNamedOnApprovalRequest.

            The LIST route keeps its perm gate. */}
        {approvalRequestDetailRoutes()}
        <Route
          path={routes.private.pipelineById}
          element={
            // `/pipelines/new` and `/pipelines/<id>` share this route.
            // The quick-action menu uses Perm.WORKFLOW_ADD to expose
            // the "new pipeline" link, so users who hold ONLY the add
            // perm could see the link and then be redirected from the
            // route. Pick the matching perm based on the route param.
            <PipelineByIdGate />
          }
        />
        {/* Launched workflows = running workflow instances, i.e. approval
            requests. Gate on request.view (NOT workflow.view — that's the
            template). Matches the backend _PROCESS_PERMS GET; the list is
            filtered to the viewer's participation server-side unless they
            hold request.view_all. */}
        <Route
          path={routes.private.processes}
          element={
            <ProtectedRoute requirePerm={Perm.REQUEST_VIEW}>
              <ProcessesList />
            </ProtectedRoute>
          }
        />
        {/* Per-workflow process list + single launched-workflow detail are
            approval-request reads (see above); request.view, matching the
            backend _PROCESS_PERMS GET. */}
        <Route
          path={routes.private.pipelineProcesses}
          element={
            <ProtectedRoute requirePerm={Perm.REQUEST_VIEW}>
              <ProcessesList />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.pipelines}
          element={
            <ProtectedRoute requirePerm={Perm.WORKFLOW_VIEW}>
              <PipelinesList />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.pipelineProcessesById}
          element={
            <ProtectedRoute requirePerm={Perm.WORKFLOW_VIEW}>
              <ProcessDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.pipelineApprovalRequest}
          element={
            <ProtectedRoute requirePerm={Perm.REQUEST_ADD}>
              <ApprovalRequestsForm />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.pipelineProcessApprovalRequest}
          element={
            <ProtectedRoute requirePerm={Perm.REQUEST_ADD}>
              <ApprovalRequestsForm />
            </ProtectedRoute>
          }
        />

        <Route path={routes.private.installTeams} element={<InstallTeamsGuide />} />

        <Route
          path={routes.private.mailbox}
          element={
            <ProtectedRoute requirePerm={Perm.MAILBOX_VIEW}>
              <Mailbox />
            </ProtectedRoute>
          }
        />

        <Route path={routes.private.assistant} element={<Assistant />}>
          <Route path=":threadId" element={null} />
        </Route>

        <Route
          path={routes.private.tasks}
          element={
            <ProtectedRoute requirePerm={Perm.TASK_VIEW}>
              <Tasks />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.taskById}
          element={
            <ProtectedRoute requirePerm={Perm.TASK_VIEW}>
              <Tasks />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.taskBoards}
          element={
            <ProtectedRoute requirePerm={Perm.TASK_VIEW}>
              <Tasks />
            </ProtectedRoute>
          }
        />
        <Route path={routes.private.version} element={<Version />} />

        <Route element={<ReportsList />}>
          <Route
            path={routes.private.reports}
            element={
              <ProtectedRoute requirePerm={Perm.REPORT_VIEW}>
                <ManualReports />
              </ProtectedRoute>
            }
          />

          <Route
            path={routes.private.aiReports}
            element={
              <ProtectedRoute onlyWithReportsAvailable requirePerm={Perm.REPORT_VIEW}>
                <AIReports />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route
          path={routes.private.createReport}
          element={
            <ProtectedRoute requirePerm={Perm.REPORT_VIEW}>
              <ReportForm />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.reportById}
          element={
            <ProtectedRoute requirePerm={Perm.REPORT_VIEW}>
              <ReportForm />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* orgs */}
      <Route
        path={routes.private.organizations}
        element={
          <ProtectedRoute onlyWithOrgsAvailable requirePerm={Perm.ACCOUNTING_MANAGE_SETTINGS}>
            <OrganisationsList />
          </ProtectedRoute>
        }
      />
      <Route
        path={routes.private.organization}
        element={
          <ProtectedRoute onlyWithOrgsAvailable requirePerm={Perm.ACCOUNTING_MANAGE_SETTINGS}>
            <OrganizationForm />
          </ProtectedRoute>
        }
      />

      {/* contacts (vendor/customer) — routes share a single list path
          that branches by ``contactParty`` URL param. The sidebar
          exposes the list via Perm.VENDOR_VIEW / Perm.CUSTOMER_VIEW;
          gating the route on ACCOUNTING_MANAGE would let view-only
          users see the nav link and then be redirected on click.
          Resolve the matching view perm from the param. */}
      <Route element={<ContactListLayout />}>
        <Route
          path={routes.private.contacts}
          element={
            <ContactsListGate>
              <VendorsList />
            </ContactsListGate>
          }
        />
        <Route
          path={routes.private.contactsList}
          element={
            <ContactsListGate>
              <VendorsList />
            </ContactsListGate>
          }
        />
      </Route>
      <Route element={<ContactLayout />}>
        <Route
          path={routes.private.contact}
          element={
            <ProtectedRoute requirePerm={Perm.ACCOUNTING_MANAGE}>
              <VendorForm />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.contactContracts}
          element={
            <ProtectedRoute requirePerm={Perm.CONTRACT_VIEW}>
              <VendorContractsList />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route
        path={routes.private.contactContract}
        element={
          <ProtectedRoute requirePerm={Perm.CONTRACT_VIEW}>
            <ContractForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={routes.private.contracts}
        element={
          <ProtectedRoute requirePerm={Perm.CONTRACT_VIEW}>
            <ContractsList />
          </ProtectedRoute>
        }
      />

      {/* invoices */}
      <Route
        path={routes.private.invoices}
        element={
          <ProtectedRoute requirePerm={Perm.INVOICE_VIEW}>
            <InvoicesList />
          </ProtectedRoute>
        }
      />
      <Route
        path={routes.private.invoiceById}
        element={
          <ProtectedRoute requirePerm={Perm.INVOICE_VIEW}>
            <InvoiceDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path={routes.private.bills}
        element={
          <ProtectedRoute requirePerm={Perm.BILL_VIEW}>
            <InvoicesList />
          </ProtectedRoute>
        }
      />
      <Route
        path={routes.private.billById}
        element={
          <ProtectedRoute requirePerm={Perm.BILL_VIEW}>
            <InvoiceDetails />
          </ProtectedRoute>
        }
      />

      {/* pos */}
      <Route
        path={routes.private.purchaseOrders}
        element={
          <ProtectedRoute requirePerm={Perm.PURCHASE_ORDER_VIEW}>
            <PurchaseOrdersList />
          </ProtectedRoute>
        }
      />
      <Route
        path={routes.private.purchaseOrdersById}
        element={
          <ProtectedRoute requirePerm={Perm.PURCHASE_ORDER_VIEW}>
            <PurchaseOrderDetails />
          </ProtectedRoute>
        }
      />

      {/* quotes */}
      <Route
        path={routes.private.quotes}
        element={
          <ProtectedRoute requirePerm={Perm.QUOTE_VIEW}>
            <QuotesList />
          </ProtectedRoute>
        }
      />
      <Route
        path={routes.private.quoteById}
        element={
          <ProtectedRoute requirePerm={Perm.QUOTE_VIEW}>
            <QuoteDetails />
          </ProtectedRoute>
        }
      />
      {/* rfqs */}
      <Route
        path={routes.private.rfqs}
        element={
          <ProtectedRoute requirePerm={Perm.RFQ_VIEW}>
            <RFQsList />
          </ProtectedRoute>
        }
      />
      <Route
        path={routes.private.rfqProposalsById}
        element={
          <ProtectedRoute requirePerm={Perm.RFQ_VIEW}>
            <RFQProposalDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path={routes.private.rfqById}
        element={
          <ProtectedRoute requirePerm={Perm.RFQ_VIEW}>
            <RFQDetails />
          </ProtectedRoute>
        }
      />

      {/* finance settings */}
      <Route element={<FinanceSettingsLayout />}>
        <Route path={routes.private.financeSettings} element={<Navigate to={routes.private.accounts} />} />
        <Route
          path={routes.private.accounts}
          element={
            <ProtectedRoute requirePerm={Perm.CHART_OF_ACCOUNTS_VIEW}>
              <AccountsList />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.accountById}
          element={
            <ProtectedRoute requirePerm={Perm.CHART_OF_ACCOUNTS_VIEW}>
              <AccountsList />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.taxes}
          element={
            <ProtectedRoute requirePerm={Perm.TAX_VIEW}>
              <TaxList />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.taxById}
          element={
            <ProtectedRoute requirePerm={Perm.TAX_VIEW}>
              <TaxList />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.products}
          element={
            <ProtectedRoute requirePerm={Perm.PRODUCT_VIEW}>
              <ProductsList />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.entities}
          element={
            <ProtectedRoute requirePerm={Perm.ACCOUNTING_MANAGE_SETTINGS}>
              <EntitiesList />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.productById}
          element={
            <ProtectedRoute requirePerm={Perm.PRODUCT_VIEW}>
              <ProductsList />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.accountCategories}
          element={
            <ProtectedRoute requirePerm={Perm.CATEGORY_VIEW}>
              <CategoriesList />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.accountCategoryById}
          element={
            <ProtectedRoute requirePerm={Perm.CATEGORY_VIEW}>
              <CategoriesList />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.private.companyDetails}
          element={
            <ProtectedRoute requirePerm={Perm.ACCOUNTING_MANAGE_SETTINGS}>
              <CompanyDetails />
            </ProtectedRoute>
          }
        />
      </Route>
      {/* cost center */}
      <Route
        path={routes.private.costCenter}
        element={
          <ProtectedRoute requirePerm={Perm.COST_CENTER_VIEW}>
            <CostCenterList />
          </ProtectedRoute>
        }
      />

      <Route
        path={routes.private.costCenterById}
        element={
          <ProtectedRoute requirePerm={Perm.COST_CENTER_VIEW}>
            <CostCenterDetails />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="budget" replace />} />
        <Route path={routes.private.costCenterBudget} element={<CostCenterDetailsBudget />} />
        <Route
          path={routes.private.costCenterBreakdown}
          element={
            <ProtectedRoute requirePerm={Perm.COST_CENTER_VIEW}>
              <CostCenterDetailsBreakdown />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route
        path={routes.private.costCenterEditById.replace(':id', 'new')}
        element={
          <ProtectedRoute requirePerm={Perm.ACCOUNTING_MANAGE}>
            <CostCenterForm />
          </ProtectedRoute>
        }
      />
      <Route
        path={routes.private.costCenterEditById}
        element={
          <ProtectedRoute requirePerm={Perm.ACCOUNTING_MANAGE}>
            <CostCenterForm />
          </ProtectedRoute>
        }
      />
    </Route>
    <Route path="*" element={<Navigate to="/404" />} />
  </Route>,
)

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter)
const router = sentryCreateBrowserRouter(Router)

export default router
